import { useDialogHostedMenuPortal } from 'module/hooks/use-dialog-hosted-overlays';
import {
  Select as PackageSelect,
  type Option as PackageSelectOption,
} from '@rocketlane/ui';
import type { CSSProperties, ReactNode } from 'react';

type CriteriaSelectValue = string | number;

export interface CriteriaSelectOption<
  TValue extends CriteriaSelectValue = CriteriaSelectValue,
> extends Omit<PackageSelectOption, 'value'> {
  content?: ReactNode;
  value: TValue;
}

interface CriteriaSelectProps<TValue extends CriteriaSelectValue> {
  className?: string;
  dataCy?: string;
  dataCyField?: string;
  disabled?: boolean;
  isSearchable?: boolean;
  menuMinWidth?: number;
  noOptionsMessage?: ReactNode;
  onChange: (value: TValue) => void;
  options: CriteriaSelectOption<TValue>[];
  placeholder?: string;
  style?: CSSProperties;
  value?: TValue;
  width?: number;
}

export const CriteriaSelect = <TValue extends CriteriaSelectValue>({
  className,
  dataCy,
  dataCyField,
  disabled,
  isSearchable = false,
  menuMinWidth,
  noOptionsMessage,
  onChange,
  options,
  placeholder = 'Select...',
  style,
  value,
  width = 160,
}: CriteriaSelectProps<TValue>) => {
  const { ref, menuPortalTarget } = useDialogHostedMenuPortal<HTMLDivElement>();
  const selectedOption =
    options.find((option) => String(option.value) === String(value)) ?? null;

  return (
    <div
      ref={ref}
      data-cy={dataCy}
      data-cy-field={dataCyField}
      style={{ minWidth: width, width, ...style }}
    >
      <PackageSelect<CriteriaSelectOption<TValue>>
        autoFocus
        className={className}
        classNames={className == null ? undefined : { menu: () => className }}
        formatOptionLabel={(option) => option.content ?? option.label}
        isDisabled={disabled}
        isSearchable={isSearchable}
        menuPortalTarget={menuPortalTarget}
        noOptionsMessage={
          noOptionsMessage == null ? undefined : () => noOptionsMessage
        }
        onChange={(option) => {
          if (option != null) {
            onChange(option.value);
          }
        }}
        options={options}
        placeholder={placeholder}
        styles={
          menuMinWidth == null
            ? undefined
            : { menu: (base) => ({ ...base, minWidth: menuMinWidth }) }
        }
        value={selectedOption}
      />
    </div>
  );
};
